import { Box } from '@mui/material';
import { groupBy, uniqBy } from 'lodash';
import React, { Fragment, MouseEvent, MouseEventHandler, useContext, useState } from 'react';
import { Contact } from 'sr-types/lib/production/v1/graphql';
import { LabelValueDisplay } from '../../attributes/AttributeValue';
import { SECURITY_ACTIONS, useSecurity } from '../../common/auth/Security';
import { UserContext } from '../../common/auth/UserContext';
import { VerticalSeparator } from '../../common/components/DelimitedLabels';
import { GROUP_BY_FIELDS } from '../../common/components/forms/groupByConfigurations/groupByConstants';
import { PUNCTUATION_MARKS_CONSTANTS } from '../../common/constants/constants';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { useI18n } from '../../common/i18n/I18n';
import ResponsiveLayout from '../../common/ResponsiveLayout';
import useHistory from '../../common/utils/useHistory';
import { getFullName } from '../../person/person';
import { PERSON_GROUP_BY } from '../../person/utils/personConstants';
import { getWorkspaceDataCardPreference } from '../DataList/ProductionDataListConfiguration/productionDataListConfigurationUtils';
import AddMore from './AddMore';
import NoContent from './NoContent';
import NoValueAvailable from './NoValueAvailable';
import { groupItemsByRole } from './productionHeaderUtils';

type AssociatedPeopleProps = {
    contacts: Contact[];
    onAdd?: MouseEventHandler<HTMLButtonElement>;
    isSaving?: boolean;
    isReadOnly?: boolean;
    groupPeopleBy?: typeof PERSON_GROUP_BY.name | typeof PERSON_GROUP_BY.none;
};

const comma = `${PUNCTUATION_MARKS_CONSTANTS.comma} `;

export default function AssociatedPeopleTile({
    contacts,
    onAdd,
    isSaving = false,
    isReadOnly = false,
    groupPeopleBy = PERSON_GROUP_BY.none
}: AssociatedPeopleProps) {
    const t = useI18n();
    const { application, preferences } = useContext(UserContext);
    const { changeRoute } = useHistory();
    const [lastVisibleIndex, setLastVisibleIndex] = useState<number | undefined>();

    const noRoleText = t('production.header.keyPeople.no.role.assigned');
    const noValueText = t('production.header.keyPeople.notAvailable');

    const { workspacePreferenceValue } = getWorkspaceDataCardPreference(preferences);

    const { allowed: canEdit } = useSecurity(SECURITY_ACTIONS.EDIT, {
        entityName: ENTITY_CONSTANTS.production
    });
    const canAddKeyPeople = !isReadOnly && canEdit;

    const groupByPreference = workspacePreferenceValue.keyPeopleGroupBy ?? GROUP_BY_FIELDS.keyPeopleName;
    const shouldShowRolesAsLabel = groupByPreference === GROUP_BY_FIELDS.keyPeopleName;
    const shouldGroupByRole = groupPeopleBy === PERSON_GROUP_BY.name && !shouldShowRolesAsLabel;

    let updatedContacts = contacts;
    let uniqueRolesMap = new Map<string, string[]>();

    if (groupPeopleBy === PERSON_GROUP_BY.name && !shouldGroupByRole) {
        const grouped = groupBy(
            contacts,
            ({ name: { firstName, lastName } }) => `${firstName}${PUNCTUATION_MARKS_CONSTANTS.pipe}${lastName}`
        );

        updatedContacts = Object.entries(grouped).map(([key, group]) => {
            const uniqueRoles = [
                ...new Set(
                    group.flatMap(({ productionRoles, roles }) => (productionRoles || roles || []).map((r) => r.trim()))
                )
            ];

            uniqueRolesMap.set(key, uniqueRoles);

            return group[0];
        });
    }

    const openPerson = (event: MouseEvent<HTMLElement>, personId?: string) => {
        event.stopPropagation();
        if (personId) changeRoute(`${application.home}/people/${personId}/details`);
    };

    const entries = shouldGroupByRole
        ? groupItemsByRole(contacts, ({ productionRoles, roles }) => productionRoles || roles, noRoleText).map(
              ({ role, items }) => {
                  const people = uniqBy(items, ({ name, personReference }) => personReference?.id ?? getFullName(name));

                  return {
                      id: role,
                      label: (
                          <>
                              {people.map(({ name, personReference }, index) => (
                                  <Fragment key={personReference?.id ?? index}>
                                      {index > 0 && comma}
                                      <Box
                                          component="span"
                                          className="clickable"
                                          onClick={(event) => openPerson(event, personReference?.id)}
                                      >
                                          {getFullName(name)}
                                      </Box>
                                  </Fragment>
                              ))}
                          </>
                      ),
                      value: role,
                      personId: people.length === 1 ? people[0].personReference?.id : undefined
                  };
              }
          )
        : updatedContacts.map((contact: Contact) => {
              const key = `${contact.name.firstName}${PUNCTUATION_MARKS_CONSTANTS.pipe}${contact.name.lastName}`;
              const roles = uniqueRolesMap.get(key);

              const rolesValue = roles?.length ? roles.join(comma) : noRoleText;
              const nameValue = getFullName(contact.name);

              return {
                  id: contact.personReference?.id,
                  label: shouldShowRolesAsLabel ? rolesValue : nameValue,
                  value: shouldShowRolesAsLabel ? nameValue : rolesValue,
                  personId: contact.personReference?.id
              };
          });

    return entries.length ? (
        <ResponsiveLayout
            gap={1.5}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            tileWidth={125}
            setLastElementToFitIndex={setLastVisibleIndex}
            fixedEndElement={
                canAddKeyPeople ? (
                    <AddMore i18nToken="production.header.keyPeople.addKeyPeople" onAdd={onAdd} disabled={isSaving} />
                ) : undefined
            }
        >
            {entries.map(({ id, label, value, personId }, index) => {
                const isInOverflow = lastVisibleIndex !== undefined && index > lastVisibleIndex;
                const showSeparator = index < entries.length - 1 && !isInOverflow;

                return (
                    <Fragment key={id}>
                        <LabelValueDisplay
                            label={label}
                            value={value}
                            labelOnTop={false}
                            onClickValue={(event) => openPerson(event, personId)}
                            wrapperSx={{ whiteSpace: 'nowrap' }}
                            labelSx={{ whiteSpace: 'break-spaces' }}
                        />
                        {showSeparator && <VerticalSeparator />}
                    </Fragment>
                );
            })}
        </ResponsiveLayout>
    ) : isReadOnly ? (
        <NoValueAvailable text={noValueText} />
    ) : canAddKeyPeople ? (
        <NoContent
            i18nToken="production.header.keyPeople.addKeyPeople"
            onAdd={onAdd}
            disabled={isSaving}
            containerSx={{
                g: 1,
                justifyContent: 'center',
                width: '220px',
                height: '43px',
                color: 'primary.main',
                '&:hover': {
                    cursor: 'pointer'
                }
            }}
        />
    ) : null;
}
